import "./calendar-days-strip.ts";
import type { CalendarReadRevision, CalendarReadValue } from "@tento-lona/sheets-ui/sheet/calendar-read-revision.ts";

import { nonnull } from "@tento-core/assert";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { CalendarDay } from "@tento-ui/components/calendar/calendar-day";
import { CalendarGesture } from "@tento-ui/components/calendar/calendar-gesture";
import { CalendarSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { SheetColumn } from "@tento-lona/sheets-ui/sheet/sheet-column.ts";
import { EventItemTimed, type Option, RowId } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import { Shimmer } from "@tento-ui/components/shimmer";
import { $$ } from "@tento-ui/fastdom";
import { LiveData } from "@tento-core/livedata";
import {
  DateTime,
  GenericRange,
  NaiveDate,
  NaiveDateTime,
  TimeOfDay,
  Utc,
  Weekday,
} from "@tento-chrono";
import { EventGhost } from "@tento-lona/sheets-ui/sheet/gestures/event-ghost.ts";
import type { PendingGhostRenderer } from "@tento-lona/sheets-ui/sheet/gestures/drag-ghost-service.ts";
import { GestureInfo } from "@tento-lona/sheets-ui/sheet/gestures/gesture-info.ts";
import type { EventItemPresentation, SheetRow } from "@tento-lona";
import { PIXELS_PER_HOUR } from "@tento-ui/components/calendar/calendar-hour";
import { CalendarDaysStrip } from "./calendar-days-strip.ts";
import {
  columnSkeletonProperties,
  PAINTS_SKELETON_ATTRIBUTE,
  SKELETON_LAYER_DECLARATIONS,
  writeColumnSkeletonSpan,
} from "./calendar-column-skeleton.ts";
import {
  columnDateAttributes,
  writeColumnDateAttributes,
} from "./calendar-sheet-column-attributes.ts";
import {
  mountRoughSweep,
  ROUGH_LAYER_CSS,
  ROUGH_LOADING_ATTRIBUTE,
  ROUGH_SHIMMER_CSS,
} from "./calendar-rough-layer.ts";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";

/**
 * One day column of a calendar sheet.
 *
 * A bound column carries its date binding as read-only host attributes:
 * `data-du-type` (the unit it spans: `day`, `week`, `month` or `year`) and
 * `data-date-start` / `data-date-end` (`yyyy-mm-dd` calendar dates in the
 * column's own timezone, half-open: `data-date-end` is the first date after
 * the column). A bind writes only the values that changed, and a recycle
 * removes them; `calendar-sheet-column-attributes.ts` holds the contract.
 */
@component({
  name: "lona-calendar-sheet-column",
})
export class CalendarSheetColumn extends LonaWebComponent {
  #$default = this.hydrate<CalendarDaysStrip>("default-root");

  #_deps: Option<CalendarSheetColumn.Dependencies> = null;
  #_signals: Option<CalendarSheetColumn.Signals> = null;
  #props: Option<CalendarSheetColumn.Props> = null;
  #dataLoadGeneration = 0;
  /** The shimmer sweep over the rough layer (`calendar-rough-shimmer`); null with the flag off or under reduced motion. */
  #sweep: Option<HTMLElement> = null;
  /** The sheet column this column paints the skeleton of, while connected under one. */
  #skeletonHost: Option<SheetColumn> = null;
  /** What the enclosing sheet column tells when it goes back to the scroll pool. */
  readonly #poolListener = Object.freeze({ columnPooled: () => this.#$default.park() });

  /** Own async data preparation before timezone resolution or either fetch. */
  beginDataLoad(): () => boolean {
    const generation = ++this.#dataLoadGeneration;
    return () => generation === this.#dataLoadGeneration;
  }

  onRecycle(): void {
    super.onRecycle();
    this.#dataLoadGeneration++;
    this.#props = null;
    writeColumnDateAttributes(this, null);
    this.#$default.clearRough();
    // A recycled column shows no content: it sweeps until its next reveal.
    if (this.#sweep) this.toggleAttribute(ROUGH_LOADING_ATTRIBUTE, true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#dataLoadGeneration++;
    this.#skeletonHost?.releaseSkeletonPainter(this);
    this.#skeletonHost?.releasePoolListener(this.#poolListener);
    this.#skeletonHost = null;
  }

  init(
    deps: CalendarSheetColumn.Dependencies,
    signals: CalendarSheetColumn.Signals,
  ) {
    this.#_deps = deps;
    this.#_signals = signals;
    this.#$default.init(deps, signals);
    this.#$default.onPresentationEnd(() => this.endLoadingPreview());
    this.#$default.adoptRoughLayer(this.$("rough"));
    this.#sweep = mountRoughSweep(this.$("rough"));
    this.#$default.onPoolQuery(() => this.inPool());
  }

  /** Whether the enclosing sheet column is parked in the scroll pool now (`[pooled]`, `scroll-column-retention`). */
  inPool(): boolean {
    return this.#skeletonHost?.hasAttribute("pooled") ?? false;
  }

  propsEq(date: NaiveDate.Partial.WithTz): boolean {
    if (!this.#props?.date) return false;
    return (
      date[0].equals(this.#props.date[0]) &&
      date[1].fullname === this.#props.date[1].fullname
    );
  }

  bind(props: CalendarSheetColumn.Props) {
    const deps = nonnull(this.#_deps, "CalendarSheetColumn.bind before init");
    nonnull(this.#_signals, "CalendarSheetColumn.bind before init");

    this.#props = props;
    writeColumnDateAttributes(this, columnDateAttributes(props.date[0]));

    this.#$default.toggleAttribute("hidden", false);

    const columnBg = deps.theme.columnBackgroundColor();
    // The span events can render in; the day-column renderer wrote it first.
    writeColumnSkeletonSpan(this, {
      date: props.date[0].start,
      zone: props.date[1],
      window: props.window,
      partialWindow: props.partialWindow,
      outside: columnBg,
      pixelsPerHour: PIXELS_PER_HOUR,
    });

    this.#$default.bind(props);
    this.#$default.style.height = PIXELS_PER_HOUR * props.window.duration.toHrsF + 0 + "px";
    this.$("rough").style.height = this.#$default.style.height;

    const hasWindow =
      props.partialWindow.start != null || props.partialWindow.end != null;
    $$.mutateEnd(() => {
      this.assignStyles({
        "--std-calendar-column-layout-horizontal-color": columnBg,
        "--column-bg": columnBg,
        ...columnSkeletonProperties({
          days: props.date[0].type === "week" ? 7 : 1,
          background: deps.theme.backgroundColor.backgroundColor(null) ?? "var(--bg)",
          innerDividers: !props.hideWeekDivider && !hasWindow,
        }),
      });

      const isDayType = props.date[0].du.type === "day";
      const notMonday = props.date[0].start.dayOfWeek !== props.startOfWeek;

      if (props.hideWeekDivider) {
        this.style.setProperty("--column-left-divider", "transparent");
      } else if (isDayType) {
        if (notMonday || hasWindow) {
          this.style.setProperty("--column-left-divider", "var(--divider)");
        } else {
          this.style.setProperty("--column-left-divider", "var(--divider-pri)");
        }
      } else {
        this.style.setProperty("--column-left-divider", "var(--divider-pri)");
      }

      this.toggleLoadingState(false, props.date);
    });
  }

  toggleLoadingState(isLoading: boolean, column: NaiveDate.Partial.WithTz) {
    this.$("default-root-bg").style.opacity = "1";
    if (isLoading) this.#$default.clearRough();
    if (isLoading && this.#sweep) this.toggleAttribute(ROUGH_LOADING_ATTRIBUTE, true);
    this.#$default.toggleLoading(isLoading, column);
  }

  /**
   * A terminal success or failure retires decoration, not the data-read state.
   * The strip's reveal ends its presentation in the write that shows its
   * chips, so the rough boxes and the shimmer sweep go in that same write.
   */
  endLoadingPreview(): void {
    this.#$default.clearRough();
    if (this.#sweep) this.removeAttribute(ROUGH_LOADING_ATTRIBUTE);
  }

  static $styles: Style.Compat[] = [
    Shimmer.$style,
    css(`#default-root-bg {${SKELETON_LAYER_DECLARATIONS}}`),
    css(ROUGH_LAYER_CSS),
    css(ROUGH_SHIMMER_CSS),
    css`
      :host {
        display: block;
        position: relative;
      }

      [hidden] {
        display: none;
      }

      [modifying] {
        opacity: 1;
        pointer-events: none;
      }

      :host::before,
      :host::after {
        pointer-events: none;
        position: absolute;
        content: "";
        top: calc(-1 * var(--divider-offset-top, 0px));
        height: calc(
          100% + var(--divider-offset-top, 0px) +
            var(--divider-offset-bottom, 0px)
        );
        background: var(--column-left-divider, var(--divider));
        width: 1px;
        z-index: 2;
      }

      :host::before {
        right: -0.5px;
        display: none;
      }

      :host::after {
        left: -0.5px;
      }

      #default-root-bg {
        position: absolute;
        top: 0px;
        left: 0px;
        right: -0.499999px;
        height: calc(100% - var(--column-tail-bottom, 12px) + 1px);
        z-index: -1;
        pointer-events: none;
      }

      /* Stale content of a column the scroll host has not rebound yet, of
       * one placed ahead of its bind, or of a held placeholder column, is
       * clipped; the skeleton stays painted. The enclosing sheet column sets
       * the mark on this host only, so only this element restyles. */
      :host([content-stale]) #default-root {
        clip-path: inset(100%);
      }

      /* Decorative; without a rule it is a hit-testable flow box. */
      #vertical-divider {
        pointer-events: none;
      }

      std-calendar-day {
        // margin-left: 1px;
      }

      std-calendar-day:first-child {
        margin-left: 0px;
      }
    `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    // The enclosing sheet column (across cell shadow roots) keeps painting
    // this skeleton while it waits for a bind.
    let node: Node | null = this.parentNode;
    while (node) {
      if (node instanceof SheetColumn) {
        node.toggleAttribute(PAINTS_SKELETON_ATTRIBUTE, true);
        if (this.#skeletonHost !== node) {
          this.#skeletonHost?.releaseSkeletonPainter(this);
          this.#skeletonHost?.releasePoolListener(this.#poolListener);
        }
        this.#skeletonHost = node;
        node.adoptSkeletonPainter(this);
        node.adoptPoolListener(this.#poolListener);
        break;
      }
      node = node instanceof ShadowRoot ? node.host : node.parentNode;
    }
  }

  static $html: HTMLTemplateElement = html`
    <div id="vertical-divider"></div>
    <div id="default-root-bg"></div>
    <div id="rough" aria-hidden="true"></div>
    <lona-calendar-days-row id="default-root" hidden></lona-calendar-days-row>
  `;
}

/**
 * Types that go with `CalendarSheetColumn`: `Type`, `Theme`, `Data`, `Props`,
 * `Dependencies`, `Signals`.
 */
export namespace CalendarSheetColumn {
  export type Type = "list" | "default";

  export type Theme = {
    backgroundColor: CalendarDay.Theme;
    columnBackgroundColor: () => string;
  };

  export type Data = {
    dates: Readonly<NaiveDate[]>;
    events: EventItemTimed[][];
  };

  export type Props = {
    readRevision: Option<CalendarReadRevision>;
    readEnvironment: () => readonly CalendarReadValue[];
    rowId: RowLocalId;
    /**
     * The cell's bound row. Used by per-cell components (e.g.
     * `EventListModal`) to discover the event-capable row via
     * `row.findRowWithFacade(EVENTS_FACADE)` without a top-down
     * `eventsRow` injection (Phase E.0b).
     */
    row: SheetRow.Joined.Handle;
    date: NaiveDate.Partial.WithTz;
    window: TimeOfDay.Range;
    partialWindow: GenericRange<Option<NaiveDateTime>>;
    subcolumns: CalendarSubcolumn[];
    hideWeekDivider: boolean;
    column: ColumnIndex.Key.Resolved.Default;
    startOfWeek: Weekday;
  };

  export type Dependencies = {
    gestureManager: CalendarGesture.Manager<GestureInfo>;
    theme: Theme;
    /**
     * Generic tooltip subsystem the layout exposes to the body's
     * more-indicator. Content rendering is renderer-owned via
     * `CalendarSubcolumn.renderOccludedTooltip`; this handle is
     * just the SheetDelegate's tooltip wrapper.
     */
    tooltip: Option<SheetDelegate.TooltipHandle>;
    /**
     * The client's event categories and drafts (`sdk.events`): the body's
     * occluded-event dots and pending-event colour, and the repaint on a
     * draft change.
     */
    presentation: EventItemPresentation.Scope;
    /**
     * The strips' shared, budgeted work queue (F1): a strip bind builds one
     * day per unit under the per-frame budget. `null` binds every day
     * synchronously in one task (the pre-F1 path).
     */
    stripWork: Option<CalendarSheetColumn.StripWork>;
    /**
     * Where strips report which days their finished binds show, for the
     * content-lag measure (`ContentLagMeter`, calendar-content-lag.ts).
     * `null`: nothing is measured.
     */
    contentLag: Option<CalendarSheetColumn.ContentLag>;
  };

  /**
   * What a strip tells the content-lag meter: which days its finished bind
   * shows, and when it shows none.
   */
  export type ContentLag = {
    /** `owner`'s bind finished (`"bind"`) or reused a finished read (`"reuse"`): it shows `days` (days since the epoch). */
    shown(owner: object, days: readonly number[], via: "bind" | "reuse"): void;
    /** `owner` shows no finished bind: a bind began, a load superseded it, it recycled, or its column parked in the pool. */
    hidden(owner: object): void;
    /** `owner`'s column data for `days` is installed: its strip bind began (day states). Absent: not kept. */
    loaded?(owner: object, days: readonly number[]): void;
    /** `owner` paints nothing now: a new date's load, a recycle or a park (day states). Absent: not kept. */
    unloaded?(owner: object): void;
    /** `owner`'s rough boxes show for `days` (`calendar-rough-layer`), until it reveals or shows nothing. Absent: not kept. */
    rough?(owner: object, days: readonly number[]): void;
  };

  /**
   * What a strip needs of the budgeted strip work queue (`StripWorkQueue` in
   * calendar-strip-work.ts): queue one bind's units, cancel a strip's units.
   */
  export type StripWork = {
    /** Queue `owner`'s units in order, after every queued unit. */
    enqueue(
      owner: object,
      units: readonly { readonly current: () => boolean; readonly run: () => void }[],
    ): void;
    /** Drop every unit `owner` has queued (and its idle registration); returns how many. */
    cancel(owner: object): number;
    /**
     * `owner`'s column parked: `start` begins its idle bind when the timeline
     * is still (`calendar-idle-complete`). Absent: nothing completes in idle.
     */
    park?(owner: object, days: { readonly first: number; readonly last: number }, start: () => void): void;
    /** Queue `owner`'s idle-bind units: they run only in idle periods. */
    enqueueIdle?(
      owner: object,
      units: readonly { readonly current: () => boolean; readonly run: () => void; readonly day?: number }[],
    ): void;
  };

  /**
   * Signals consumed by the body web component (`CalendarDaysStrip`).
   * Cursor + gesture-broadcast state lives on the chrome bus
   * (`CalendarChromeBus`) instead — those signals don't belong here.
   */
  export type Signals = {
    currentTime: LiveData.Readable<DateTime<Utc>>;
    currentGestureCount: LiveData.Readable<number>;
    /**
     * Register a pending-event renderer (drag-to-create / drag-to-move
     * ghost paint). Returns a disposer the caller MUST invoke on
     * disconnect — passing back-references to the host avoids the
     * old single-slot wipe race that left the visible week's row
     * unregistered after an off-screen days-row recycled.
     */
    addPendingEventRenderer: (
      renderer: PendingGhostRenderer,
    ) => () => void;
  };
}
